import { Link } from "react-router-dom";
import { LogIn, LogOut, LayoutDashboard } from "lucide-react";
import {
  LANDING_NAV_LINKS,
  scrollToSection,
} from "./navConfig";
import { useAuth } from "@/contexts/AuthContext";

interface LandingMobileMenuProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Mobile dropdown for the landing page header. Driven by the same
 * `LANDING_NAV_LINKS` source as the desktop nav so the two stay in sync.
 *
 * Visual style mirrors the authenticated UserSidebar: rounded item rows,
 * leading icons, hover background fill — for a consistent feel between
 * the marketing site and the in-app experience.
 */
export function LandingMobileMenu({
  open,
  onClose,
}: LandingMobileMenuProps) {
  if (!open) return null;
  return <LandingMobileMenuInner onClose={onClose} />;
}

function LandingMobileMenuInner({
  onClose,
}: {
  onClose: () => void;
}) {
  const { user, signOut } = useAuth();

  const itemClass =
    "flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-md text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors duration-200";

  return (
    // No top border here — the sticky <header> already renders `border-b`,
    // so adding another would visually double the divider when open.
    <div className="lg:hidden bg-background shadow-sm">
      <nav className="px-3 pt-3 pb-2 space-y-1" aria-label="Mobile primary">
        {LANDING_NAV_LINKS.map((link) => {
          const Icon = link.icon;
          return (
            <button
              key={link.label}
              onClick={() => {
                scrollToSection(link.id);
                onClose();
              }}
              className={itemClass}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {link.label}
            </button>
          );
        })}
      </nav>
      <div className="px-3 pt-2 pb-3 mt-1 border-t border-border space-y-2">
        {user ? (
          <>
            <Link to="/dashboard" onClick={onClose} className={itemClass}>
              <LayoutDashboard className="h-4 w-4 shrink-0" />
              Open Application
            </Link>
            <button
              type="button"
              onClick={() => { void signOut(); onClose(); }}
              className={itemClass}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Sign Out
            </button>
          </>
        ) : (
          <Link to="/login" onClick={onClose} className={itemClass}>
            <LogIn className="h-4 w-4 shrink-0" />
            Sign In
          </Link>
        )}
      </div>
    </div>
  );
}